import { Scene } from "../core/Scene";
import type { Direction } from "../core/Input";
import { ChestModel } from "../render/ChestModel";
import { MonsterModel } from "../render/MonsterModel";
import { Actor, NpcActor, Trail } from "../world/Actor";
import { CameraRig } from "../world/CameraRig";
import type { Circle } from "../world/Collision";
import { FieldMonster } from "../world/FieldMonster";
import { MapRenderer } from "../world/MapRenderer";
import { ACTOR_SCALE, TILE, tileToWorld } from "../world/constants";
import {
  DIRECTION_ANGLES,
  TileMap,
  type ChestDef,
  type EncounterGroup,
  type ExitDef,
  type NpcDef,
} from "../world/TileMap";
import { Monster } from "../rpg/Monster";
import type { BattleOutcome } from "../rpg/Battle";
import type { World, InteractionContext } from "../rpg/World";
import { GameWindow } from "../ui/Window";
import { PartyMenu } from "./PartyMenu";
import { BattleScene } from "./BattleScene";

/** How close the player has to be to talk to something, in world units. */
const INTERACT_RANGE = TILE * 1.25;
/** …and how far off dead-ahead it may be. cos(60°). */
const INTERACT_COS = 0.5;

/** Gap between party members along the leader's trail. */
const FOLLOW_SPACING = TILE * 0.8;
/** How far each follower stands off to the side of that trail. */
const FOLLOW_LATERAL = TILE * 0.55;

interface ChestInstance {
  def: ChestDef;
  model: ChestModel;
  x: number;
  z: number;
}

interface Interactable {
  kind: "npc" | "chest";
  npc?: NpcActor;
  chest?: ChestInstance;
  label: string;
  x: number;
  z: number;
}

/**
 * Free-roam field: a third-person camera behind the hero, continuous movement,
 * a party that files along behind, and monsters you can see coming.
 */
export class FieldScene extends Scene {
  private map!: TileMap;
  private mapRenderer!: MapRenderer;
  private player!: Actor;
  private followers: Actor[] = [];
  private trail = new Trail(0.25);
  private npcs: NpcActor[] = [];
  private npcDefs = new Map<NpcActor, NpcDef>();
  private chests: ChestInstance[] = [];
  private monsters: FieldMonster[] = [];
  /** Decorative monster bodies attached to NPCs; ticked for their idle motion. */
  private monsterProps: MonsterModel[] = [];
  private respawnQueue: { at: number; group: EncounterGroup }[] = [];

  private rig = new CameraRig();
  private statusWindow!: GameWindow;
  private statusCache = "";
  private promptWindow!: GameWindow;
  private promptCache = "";
  private banner: GameWindow | null = null;
  private bannerTimer = 0;

  /** Blocks player input while dialogue, a menu, a transition or a battle runs. */
  private busy = false;
  /** Distance still to walk before encounters can trigger again. */
  private grace = 0;
  private elapsed = 0;
  /** Tile the player was in last frame, for exit detection. */
  private lastTile = { x: -1, y: -1 };

  constructor(private world: World) {
    super();
    this.camera = this.rig.camera;
  }

  async enter(): Promise<void> {
    this.statusWindow = this.game.ui.window("", "ds-status");
    this.promptWindow = this.game.ui.window("", "ds-prompt");
    this.setHint();
    this.loadMap();
    await this.game.ui.fadeIn(350);
  }

  exit(): void {
    this.teardownMap();
    this.statusWindow.destroy();
    this.promptWindow.destroy();
    this.banner?.destroy();
    this.game.ui.setHint("");
    this.game.input.exitPointerLock();
  }

  pause(): void {
    this.statusWindow.hide();
    this.promptWindow.hide();
    this.game.input.pointerLockEnabled = false;
    this.game.input.exitPointerLock();
  }

  resume(): void {
    this.statusWindow.show();
    this.refreshStatus(true);
    this.playMapMusic();
    this.setHint();
    this.game.input.pointerLockEnabled = true;
  }

  resize(width: number, height: number): void {
    this.rig.resize(width, height);
  }

  private setHint(): void {
    this.game.ui.setHint("WASD move · mouse look · wheel zoom · Z talk · C menu · M mute");
  }

  // ------------------------------------------------------------ map loading

  private loadMap(): void {
    const def = this.world.currentMap;
    this.map = new TileMap(def, this.world.tileset);
    this.mapRenderer = new MapRenderer(this.map);
    this.scene.add(this.mapRenderer.object3D);
    this.mapRenderer.applyEnvironment(this.scene);

    const { x, y, facing } = this.world.location;
    this.player = new Actor({
      x,
      y,
      facing,
      appearance: this.world.party.leader.appearance,
      name: this.world.party.leader.name,
      speed: 4.4,
    });
    this.scene.add(this.player.object3D);
    this.lastTile = { x: this.player.tileX, y: this.player.tileY };

    // Line the rest of the party up behind the leader, facing the same way.
    const angle = DIRECTION_ANGLES[facing];
    const back = { x: -Math.sin(angle), z: -Math.cos(angle) };
    this.trail.reset(this.player.x, this.player.z);
    this.followers = this.world.party.members.slice(1).map((member, i) => {
      const follower = new Actor({
        x,
        y,
        facing,
        appearance: member.appearance,
        name: member.name,
        solid: false,
        speed: 5.0,
      });
      const offset = FOLLOW_SPACING * (i + 1);
      const side = (i % 2 === 0 ? 1 : -1) * FOLLOW_LATERAL;
      follower.setWorldPosition(
        this.player.x + back.x * offset - back.z * side,
        this.player.z + back.z * offset + back.x * side,
      );
      this.scene.add(follower.object3D);
      return follower;
    });

    for (const npcDef of def.npcs ?? []) this.addNpc(npcDef);
    for (const chestDef of def.chests ?? []) this.addChest(chestDef);
    this.populateMonsters();

    this.rig.snapBehind(angle, this.player.object3D.position);
    this.playMapMusic();
    this.refreshStatus(true);
    this.grace = TILE * 2;
    if (def.title) this.showBanner(def.title);
  }

  private teardownMap(): void {
    for (const actor of [this.player, ...this.followers, ...this.npcs]) actor?.dispose();
    for (const chest of this.chests) chest.model.dispose();
    for (const monster of this.monsters) monster.dispose();
    for (const prop of this.monsterProps) prop.dispose();
    this.mapRenderer?.dispose();
    this.disposeGraph();
    this.scene.clear();
    this.scene.background = null;
    this.scene.fog = null;
    this.followers = [];
    this.npcs = [];
    this.npcDefs.clear();
    this.chests = [];
    this.monsters = [];
    this.monsterProps = [];
    this.respawnQueue = [];
    this.promptCache = "";
    this.promptWindow.hide();
  }

  private addNpc(def: NpcDef): void {
    const party = this.world.party;
    if (def.requiresFlag && !party.hasFlag(def.requiresFlag)) return;
    if (def.hiddenByFlag && party.hasFlag(def.hiddenByFlag)) return;

    const npc = new NpcActor({
      x: def.x,
      y: def.y,
      facing: def.facing ?? "down",
      appearance: def.monster ? undefined : def.appearance,
      name: def.name ?? "",
      movement: def.monster ? "fixed" : (def.movement ?? "fixed"),
      lines: def.lines ?? [],
      tag: def.tag,
      turnsToPlayer: def.monster ? false : (def.turnsToPlayer ?? true),
    });

    if (def.monster) {
      // A creature standing on the map — a boss, or set dressing. It gets a
      // body and an idle animation, but none of a field monster's behaviour.
      const model = new MonsterModel(def.monster);
      model.object3D.scale.multiplyScalar(ACTOR_SCALE);
      npc.object3D.add(model.object3D);
      npc.radius = 0.5 * ACTOR_SCALE * (def.monster.scale ?? 1);
      this.monsterProps.push(model);
    }

    this.scene.add(npc.object3D);
    this.npcs.push(npc);
    this.npcDefs.set(npc, def);
  }

  private addChest(def: ChestDef): void {
    const model = new ChestModel();
    model.object3D.scale.setScalar(ACTOR_SCALE);
    const x = tileToWorld(def.x);
    const z = tileToWorld(def.y);
    model.object3D.position.set(x, 0, z);
    if (this.world.party.hasFlag(this.chestFlag(def))) model.setOpenImmediate();
    this.scene.add(model.object3D);
    this.chests.push({ def, model, x, z });
  }

  private chestFlag(def: ChestDef): string {
    return def.flag ?? `chest:${this.world.location.map}:${def.x}:${def.y}`;
  }

  private playMapMusic(): void {
    const song = this.world.currentMap.music;
    if (song && this.world.songs[song]) this.game.audio.playSong(this.world.songs[song]);
    else this.game.audio.stopSong();
  }

  // ------------------------------------------------------- symbol encounters

  private get encounterTable() {
    return this.world.currentMap.encounter;
  }

  private get usesSymbols(): boolean {
    const table = this.encounterTable;
    return !!table && (table.mode ?? "symbols") === "symbols" && table.groups.length > 0;
  }

  /** Fills the map with roaming monsters at load time. */
  private populateMonsters(): void {
    const table = this.encounterTable;
    if (!this.usesSymbols || !table) return;
    const wanted = table.maxActive ?? 6;
    for (let i = 0; i < wanted; i++) this.spawnMonster();
  }

  private spawnMonster(): void {
    const table = this.encounterTable;
    if (!table) return;
    const group = this.game.rng.weighted(table.groups, (g) => g.weight ?? 1);
    const spot = this.findSpawnPoint();
    if (!spot) return;

    const lead = this.world.monsters.get(group.monsters[0].id);
    const monster = new FieldMonster({
      x: spot.x,
      z: spot.z,
      group,
      lead,
      // Left undefined unless the map says otherwise, so the monster's own
      // default applies rather than a second fallback hidden in here.
      aggroRadius: table.aggro,
    });
    this.scene.add(monster.object3D);
    this.monsters.push(monster);
  }

  /** A walkable, encounter-enabled tile that is not right on top of the party. */
  private findSpawnPoint(): { x: number; z: number } | null {
    const rng = this.game.rng;
    for (let attempt = 0; attempt < 80; attempt++) {
      const tx = rng.int(0, this.map.width - 1);
      const ty = rng.int(0, this.map.height - 1);
      if (this.map.isSolid(tx, ty)) continue;
      if (this.map.encounterScaleAt(tx, ty) <= 0) continue;
      if (this.map.exitAt(tx, ty)) continue;

      const x = tileToWorld(tx);
      const z = tileToWorld(ty);
      if (Math.hypot(x - this.player.x, z - this.player.z) < TILE * 6) continue;
      return { x, z };
    }
    return null;
  }

  // ------------------------------------------------------------------ frame

  update(dt: number): void {
    this.elapsed += dt;
    this.game.audio.update(dt);

    if (!this.busy) {
      this.handleCamera();
      this.handleMovement(dt);
      this.handleActions();
    }

    this.player.update(dt);
    this.updateFollowers(dt);

    const obstacles = this.obstacles();
    for (const npc of this.npcs) {
      if (!this.busy) npc.tick(dt, this.isSolid, obstacles);
      npc.update(dt);
    }
    for (const prop of this.monsterProps) prop.update(dt);
    for (const chest of this.chests) chest.model.update(dt);

    if (!this.busy) this.updateMonsters(dt);

    this.mapRenderer.update(dt);
    this.mapRenderer.setFocus(this.player.x, this.player.z);
    this.rig.update(
      dt,
      this.player.object3D.position,
      this.player.isMoving ? this.player.angle : null,
      this.mapRenderer.solidMeshes,
    );

    this.refreshStatus(false);
    this.refreshPrompt();

    if (this.banner) {
      this.bannerTimer -= dt;
      if (this.bannerTimer <= 0) {
        this.banner.destroy();
        this.banner = null;
      }
    }
  }

  private handleCamera(): void {
    const look = this.game.input.look();
    this.rig.look(look.x, look.y);
    this.rig.zoom(this.game.input.zoom());
  }

  private handleMovement(dt: number): void {
    const input = this.game.input;
    const axis = input.moveAxis();

    if (axis.magnitude < 0.05) return;

    // Movement is relative to where the camera is looking, which is what makes
    // an orbit camera feel like a modern third-person game rather than a maze.
    const forward = this.rig.forward;
    const right = this.rig.right;
    const dx = forward.x * axis.y + right.x * axis.x;
    const dz = forward.z * axis.y + right.z * axis.x;
    const length = Math.hypot(dx, dz) || 1;

    const before = { x: this.player.x, z: this.player.z };
    this.player.speed = input.held("run") ? 6.6 : 4.2;
    this.player.walk(dx / length, dz / length, dt, this.isSolid, this.obstacles(this.player), axis.magnitude);

    const travelled = Math.hypot(this.player.x - before.x, this.player.z - before.z);
    if (travelled <= 0) return;

    this.world.party.steps += travelled / TILE;
    this.grace = Math.max(0, this.grace - travelled);
    this.trail.push(this.player.x, this.player.z);

    this.onMoved(travelled);
  }

  private updateFollowers(dt: number): void {
    const obstacles = this.obstacles();
    this.followers.forEach((follower, i) => {
      const target = this.followTarget(i);
      if (!target) return;
      const distance = Math.hypot(target.x - follower.x, target.z - follower.z);
      // A small dead zone stops them shuffling on the spot when the leader
      // stands still.
      if (distance > 0.12) {
        const speed = Math.min(9, Math.max(this.player.speed, distance * 4));
        follower.approach(target.x, target.z, dt, this.isSolid, obstacles, speed);
      }
      follower.update(dt);
    });
  }

  /**
   * Where follower `i` wants to stand: back along the leader's path, then
   * pushed out to one side. Without the sideways offset the party queues up
   * directly between the hero and the camera and you spend the game looking at
   * the back of a hat.
   */
  private followTarget(i: number): { x: number; z: number } | null {
    const back = FOLLOW_SPACING * (i + 1);
    const point = this.trail.sample(back);
    if (!point) return null;

    // Direction of travel at that point on the trail.
    const behind = this.trail.sample(back + 0.6) ?? point;
    let dx = point.x - behind.x;
    let dz = point.z - behind.z;
    const length = Math.hypot(dx, dz);
    if (length < 1e-3) {
      dx = Math.sin(this.player.angle);
      dz = Math.cos(this.player.angle);
    } else {
      dx /= length;
      dz /= length;
    }

    const side = (i % 2 === 0 ? 1 : -1) * FOLLOW_LATERAL;
    return { x: point.x + dz * side, z: point.z - dx * side };
  }

  private handleActions(): void {
    const input = this.game.input;

    if (input.consume("menu")) {
      void this.openMenu();
      return;
    }
    if (input.consume("confirm")) {
      void this.interact();
      return;
    }
    if (input.consume("debug")) this.game.audio.unlock();
  }

  private readonly isSolid = (tileX: number, tileY: number): boolean => this.map.isSolid(tileX, tileY);

  /** Circles that block movement: NPCs and unopened chests. */
  private obstacles(exclude?: Actor): Circle[] {
    const list: Circle[] = [];
    for (const npc of this.npcs) {
      if (npc === exclude || !npc.solid) continue;
      list.push(npc.asCircle());
    }
    for (const chest of this.chests) {
      list.push({ x: chest.x, z: chest.z, radius: 0.5 });
    }
    if (exclude !== this.player && this.player) list.push(this.player.asCircle());
    return list;
  }

  /** Called after the player actually moved: exits, footsteps, encounters. */
  private onMoved(distance: number): void {
    this.world.location.x = this.player.tileX;
    this.world.location.y = this.player.tileY;

    const tx = this.player.tileX;
    const ty = this.player.tileY;
    if (tx !== this.lastTile.x || ty !== this.lastTile.y) {
      this.lastTile = { x: tx, y: ty };
      const tile = this.map.at(tx, ty);
      this.game.audio.play(tile?.step ?? "step");

      const exit = this.map.exitAt(tx, ty);
      if (exit && !exit.manual) {
        void this.travelThrough(exit);
        return;
      }
    }

    // Legacy random encounters, for maps that ask for them.
    const table = this.encounterTable;
    if (!table || this.usesSymbols || table.groups.length === 0) return;
    if (this.grace > 0) return;
    const chance = (table.rate ?? 0) * this.map.encounterScaleAt(tx, ty) * (distance / TILE);
    if (chance > 0 && this.game.rng.chance(chance)) {
      const group = this.game.rng.weighted(table.groups, (g) => g.weight ?? 1);
      void this.startBattle(group);
    }
  }

  private updateMonsters(dt: number): void {
    const player = { x: this.player.x, z: this.player.z };
    // Deliberately excluding the player: a monster that collides with the
    // party can never actually reach them, and the whole point is to be caught.
    const obstacles = this.obstacles(this.player);

    for (const monster of this.monsters) {
      monster.update(dt, player, this.isSolid, obstacles);
      if (monster.defeated || this.busy) continue;

      const reach = monster.radius + this.player.radius;
      if (this.grace <= 0 && monster.distanceTo(player) < reach) {
        void this.engage(monster);
        return;
      }
    }

    // Replace anything the party has killed, after a decent pause.
    const table = this.encounterTable;
    if (!table) return;
    while (this.respawnQueue.length > 0 && this.respawnQueue[0].at <= this.elapsed) {
      this.respawnQueue.shift();
      if (this.monsters.length < (table.maxActive ?? 6)) this.spawnMonster();
    }
  }

  private removeMonster(monster: FieldMonster): void {
    this.scene.remove(monster.object3D);
    monster.dispose();
    this.monsters = this.monsters.filter((m) => m !== monster);
  }

  /** Runs the battle a field monster walked the party into. */
  private async engage(monster: FieldMonster): Promise<void> {
    await this.runBusy(async () => {
      const outcome = await this.runBattle(monster.group);
      const table = this.encounterTable;

      if (outcome === "win") {
        this.removeMonster(monster);
        this.respawnQueue.push({ at: this.elapsed + (table?.respawn ?? 25), group: monster.group });
      } else {
        // Fled or lost: give the party a moment to get clear.
        for (const m of this.monsters) {
          if (m.distanceTo(this.player) < TILE * 6) m.cool(6);
        }
        this.grace = TILE * 3;
      }
      this.playMapMusic();
    });
  }

  // ------------------------------------------------------------ interaction

  /** Nearest thing in front of the player that can be talked to or opened. */
  private findInteractable(): Interactable | null {
    const facingX = Math.sin(this.player.angle);
    const facingZ = Math.cos(this.player.angle);

    let best: Interactable | null = null;
    let bestScore = Infinity;

    const consider = (candidate: Interactable) => {
      const dx = candidate.x - this.player.x;
      const dz = candidate.z - this.player.z;
      const distance = Math.hypot(dx, dz);
      if (distance > INTERACT_RANGE) return;
      // Dead ahead wins over merely nearby.
      const dot = distance < 1e-3 ? 1 : (dx * facingX + dz * facingZ) / distance;
      if (dot < INTERACT_COS) return;
      const score = distance - dot;
      if (score < bestScore) {
        bestScore = score;
        best = candidate;
      }
    };

    for (const npc of this.npcs) {
      consider({ kind: "npc", npc, label: npc.name || "Talk", x: npc.x, z: npc.z });
    }
    for (const chest of this.chests) {
      const looted = this.world.party.hasFlag(this.chestFlag(chest.def));
      consider({ kind: "chest", chest, label: looted ? "Empty chest" : "Open", x: chest.x, z: chest.z });
    }
    return best;
  }

  private refreshPrompt(): void {
    if (this.busy) {
      if (this.promptCache !== "") {
        this.promptCache = "";
        this.promptWindow.hide();
      }
      return;
    }

    const target = this.findInteractable();
    const text = target ? `Z   ${target.label}` : "";
    if (text === this.promptCache) return;
    this.promptCache = text;
    if (text) this.promptWindow.setText(text).show();
    else this.promptWindow.hide();
  }

  private async interact(): Promise<void> {
    const target = this.findInteractable();
    if (!target) return;
    if (target.kind === "npc" && target.npc) await this.talkTo(target.npc);
    else if (target.chest) await this.openChest(target.chest);
  }

  private async talkTo(npc: NpcActor): Promise<void> {
    await this.runBusy(async () => {
      npc.frozen = true;
      if (npc.turnsToPlayer) npc.faceTowards(this.player.x, this.player.z);
      this.game.audio.play("confirm");

      const def = this.npcDefs.get(npc) ?? {
        x: npc.tileX,
        y: npc.tileY,
        lines: npc.lines,
        tag: npc.tag,
      };
      const handled = this.world.onInteract
        ? await this.world.onInteract(def, this.interactionContext())
        : false;

      if (!handled) {
        for (const line of npc.lines) await this.game.ui.message.show(line);
      }
      this.game.ui.message.hide();
      npc.frozen = false;
    });
  }

  private async openChest(chest: ChestInstance): Promise<void> {
    await this.runBusy(async () => {
      const flag = this.chestFlag(chest.def);
      if (this.world.party.hasFlag(flag)) {
        await this.game.ui.message.show("The chest is empty.");
        this.game.ui.message.hide();
        return;
      }

      chest.model.open();
      this.world.party.setFlag(flag);
      this.game.audio.play("chest");

      if (chest.def.item) {
        const item = this.world.items.get(chest.def.item);
        this.world.party.addItem(item.id);
        await this.game.ui.message.show(`Inside is ${item.name}!\n${item.description}`);
      }
      if (chest.def.gold) {
        this.world.party.gold += chest.def.gold;
        await this.game.ui.message.show(`Inside are ${chest.def.gold} gold coins!`);
      }
      this.game.ui.message.hide();
      this.refreshStatus(true);
    });
  }

  private interactionContext(): InteractionContext {
    const ui = this.game.ui;
    return {
      world: this.world,
      say: (text: string) => ui.message.show(text),
      ask: async (question: string) => {
        ui.message.set(question);
        const answer = await ui.menu().open({
          items: [{ label: "Yes" }, { label: "No" }],
          anchor: { right: "8%", bottom: "26%" },
        });
        ui.message.hide();
        return answer === 0;
      },
      choose: async (title: string, options: string[]) =>
        ui.menu().open({
          title,
          items: options.map((label) => ({ label })),
          anchor: { right: "8%", bottom: "26%" },
        }),
      fight: async (monsters: string[]) => {
        ui.message.hide();
        const outcome = await this.runBattle({ monsters: monsters.map((id) => ({ id })) });
        this.playMapMusic();
        return outcome;
      },
      reload: () => this.reload(),
    };
  }

  private async openMenu(): Promise<void> {
    await this.runBusy(async () => {
      this.game.audio.play("confirm");
      await new PartyMenu(this.world, this.game.ui, this.game.rng, this.game.audio).open();
      this.game.ui.message.hide();
      this.refreshStatus(true);
    });
  }

  /** Runs an async interaction with player input and the pointer lock suspended. */
  private async runBusy(fn: () => Promise<void> | void): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.promptWindow.hide();
    this.promptCache = "";
    this.game.input.pointerLockEnabled = false;
    this.game.input.exitPointerLock();
    try {
      await fn();
    } finally {
      this.busy = false;
      this.game.input.pointerLockEnabled = true;
      this.game.input.clearPressed();
    }
  }

  // ----------------------------------------------------------------- travel

  private async travelThrough(exit: ExitDef): Promise<void> {
    await this.travel(exit.to, exit.toX, exit.toY, exit.facing);
  }

  private async travel(mapId: string, x: number, y: number, facing?: Direction): Promise<void> {
    await this.runBusy(async () => {
      await this.game.ui.fadeOut(260);
      this.teardownMap();
      this.world.moveTo(mapId, x, y, facing);
      this.loadMap();
      await this.game.ui.fadeIn(260);
    });
  }

  private showBanner(title: string): void {
    this.banner?.destroy();
    this.banner = this.game.ui
      .window("", "ds-banner")
      .anchor({ top: "8%", left: "50%", transform: "translateX(-50%)" })
      .setText(title)
      .show();
    this.bannerTimer = 2.4;
  }

  // ---------------------------------------------------------------- battles

  private async startBattle(group: EncounterGroup): Promise<void> {
    await this.runBusy(async () => {
      await this.runBattle(group);
      this.grace = TILE * 3;
      this.playMapMusic();
    });
  }

  /**
   * Runs a battle to completion and returns the outcome. Assumes the caller
   * has already suspended field input.
   */
  private async runBattle(group: EncounterGroup): Promise<BattleOutcome> {
    const monsters: Monster[] = [];
    for (const slot of group.monsters) {
      const def = this.world.monsters.get(slot.id);
      const count = Array.isArray(slot.count)
        ? this.game.rng.int(slot.count[0], slot.count[1])
        : (slot.count ?? 1);
      for (let i = 0; i < count; i++) monsters.push(new Monster(def));
    }
    if (monsters.length === 0) return "fled";

    this.game.audio.stopSong();
    this.game.audio.play("encounter");
    await this.game.ui.flash(2, 80);

    const battle = new BattleScene(this.world, monsters, this.battleEnvironment());
    await this.game.push(battle);
    const outcome = await battle.finished;

    this.grace = TILE * 3;
    this.refreshStatus(true);

    if (outcome === "lose") await this.handleDefeat();
    return outcome;
  }

  /** The battle arena borrows the light and colour of wherever you were standing. */
  private battleEnvironment() {
    const def = this.world.currentMap;
    const tile = this.map.at(this.player.tileX, this.player.tileY);
    return {
      ground: tile?.color ?? 0x3f7a3f,
      sky: def.sky ?? 0x0c1024,
      ambient: def.ambient ?? 1.0,
      sunColor: def.sunColor ?? 0xffe8c0,
      sunIntensity: def.sunIntensity ?? 1.5,
    };
  }

  /** Rebuilds the current map in place — after the party roster changes, say. */
  private reload(): void {
    this.teardownMap();
    this.loadMap();
  }

  private async handleDefeat(): Promise<void> {
    await this.game.ui.fadeOut(600);
    this.game.audio.play("gameover");
    await this.game.ui.message.show("The party has fallen…");

    const lost = Math.floor(this.world.party.gold / 2);
    this.world.party.gold -= lost;
    this.world.party.reviveAll();
    this.world.party.fullHeal();

    this.teardownMap();
    const { map, x, y } = this.world.respawn;
    this.world.moveTo(map, x, y, "down");
    this.loadMap();

    await this.game.ui.message.show(
      `You wake at the inn, tended by strangers.\nThe innkeeper took ${lost} gold for the trouble.`,
    );
    this.game.ui.message.hide();
    await this.game.ui.fadeIn(500);
  }

  // ------------------------------------------------------------------- HUD

  private refreshStatus(force: boolean): void {
    const party = this.world.party;
    const rows = party.members
      .map((m) => {
        const low = m.hp <= m.maxHp * 0.25;
        const hp = `<span class="${low ? "ds-hp-low" : ""}">${m.hp}/${m.maxHp}</span>`;
        return `<div><span class="name">${m.name}</span> Lv${m.level}<br>HP ${hp}  MP ${m.mp}/${m.maxMp}</div>`;
      })
      .join("");
    const html = `${rows}<div>Gold ${party.gold}</div>`;

    if (!force && html === this.statusCache) return;
    this.statusCache = html;
    this.statusWindow.setHTML(html).show();
  }
}
